import { useState, useEffect } from 'react';
import { useChat } from '../context/ChatContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Textarea } from './ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  MessageSquare, 
  Sparkles, 
  Copy, 
  RefreshCw,
  Users,
  Target,
  Calendar,
  Gift,
  Bell,
  Heart,
  AlertTriangle,
  Crown,
  Loader2,
  Check
} from 'lucide-react';
import axios from 'axios';

import { API_BASE_URL as API } from '../utils/apiConfig';

// SMS Templates by category
const smsTemplates = {
  welcome: {
    english: [
      "Welcome to {brand}! 🎉 You've earned {points} points on your first purchase. Shop again & earn more rewards! T&C apply.",
      "Hi {name}! Thanks for joining {brand} Loyalty. Your member ID: {id}. Enjoy exclusive offers & earn points on every purchase!",
      "Welcome aboard! 🌟 You're now part of {brand} family. Start earning points today & unlock amazing rewards!"
    ],
    hinglish: [
      "Welcome to {brand}! 🎉 Aapko first purchase par {points} points mile hain. Dobara shopping karein aur rewards paayein! T&C apply.",
      "Hi {name}! {brand} Loyalty mein aapka swagat hai. Member ID: {id}. Har purchase par points kamayein aur offers enjoy karein!",
      "Swagat hai! 🌟 Ab aap {brand} family ka hissa hain. Aaj se points kamana shuru karein aur amazing rewards paayen!"
    ]
  },
  winback: {
    english: [
      "We miss you {name}! 💫 It's been a while. Come back & get FLAT 20% OFF on your next purchase. Use code: COMEBACK. Valid till {date}",
      "Hey {name}! Your {points} points are waiting! Don't let them expire. Shop now & redeem rewards. Visit {brand} today!",
      "{name}, we noticed you haven't visited in a while. Here's a special offer just for you - Extra {points} bonus points on next purchase!"
    ],
    hinglish: [
      "Aapki yaad aa rahi hai {name}! 💫 Bahut din ho gaye. Wapas aayein aur FLAT 20% OFF paayein. Code: COMEBACK use karein. {date} tak valid",
      "Hey {name}! Aapke {points} points wait kar rahe hain! Expire hone se pehle redeem karein. Aaj hi {brand} visit karein!",
      "{name}, bahut din se aapki visit nahi hui. Sirf aapke liye special offer - Next purchase par Extra {points} bonus points!"
    ]
  },
  birthday: {
    english: [
      "Happy Birthday {name}! 🎂 Celebrate with {brand}. Enjoy 2X points on all purchases this week. Make your day special!",
      "🎉 It's your special day {name}! {brand} wishes you a very Happy Birthday. Gift yourself with our exclusive Birthday offer - {discount}% OFF!",
      "Birthday wishes from {brand}! 🎁 Your birthday gift: {points} bonus points credited. Valid for 7 days. Enjoy your celebration!"
    ],
    hinglish: [
      "Happy Birthday {name}! 🎂 {brand} ke saath celebrate karein. Is hafte sabhi purchases par 2X points paayein!",
      "🎉 Aaj aapka special din hai {name}! {brand} ki taraf se bahut bahut badhai. Birthday special - {discount}% OFF!",
      "{brand} ki taraf se Birthday wishes! 🎁 Aapki gift: {points} bonus points credit ho gaye. 7 din valid. Enjoy karein!"
    ]
  },
  pointsExpiry: {
    english: [
      "⚠️ Alert {name}! Your {points} points expire on {date}. Don't lose them - visit {brand} and redeem now!",
      "{name}, your loyalty points ({points}) are expiring soon! Shop before {date} to use them. Every point counts!",
      "Reminder: {points} points expiring in 7 days! Visit {brand} to redeem your rewards before they're gone. T&C apply."
    ],
    hinglish: [
      "⚠️ Alert {name}! Aapke {points} points {date} ko expire ho jayenge. Abhi {brand} visit karein aur redeem karein!",
      "{name}, aapke loyalty points ({points}) jaldi expire hone wale hain! {date} se pehle use karein. Har point matters!",
      "Yaad dilana: {points} points 7 din mein expire! {brand} visit karein aur rewards le jaayein. T&C apply."
    ]
  },
  campaign: {
    english: [
      "🔥 {campaign_name} is LIVE! Shop at {brand} & get {discount}% OFF + {points}X points. Limited period offer. Hurry!",
      "Exclusive for you! {brand} {campaign_name} - Flat {discount}% OFF on all products. Use code: {code}. Valid till {date}",
      "⭐ Members-only sale at {brand}! Extra {discount}% OFF + Double points. Don't miss out! T&C apply."
    ],
    hinglish: [
      "🔥 {campaign_name} LIVE hai! {brand} par shopping karein & {discount}% OFF + {points}X points paayein. Jaldi karein!",
      "Sirf aapke liye! {brand} {campaign_name} - Sabhi products par Flat {discount}% OFF. Code: {code}. {date} tak valid",
      "⭐ Members-only sale {brand} par! Extra {discount}% OFF + Double points. Miss mat karein! T&C apply."
    ]
  },
  tierUpgrade: {
    english: [
      "Congratulations {name}! 🏆 You've been upgraded to {tier} tier at {brand}. Enjoy exclusive benefits & more rewards!",
      "Level up! 🎊 {name}, you're now a {tier} member. Unlock premium perks: {benefits}. Thank you for your loyalty!",
      "🌟 {name}, your loyalty paid off! Welcome to {tier} tier. Enjoy {discount}% extra on all purchases. Keep shining!"
    ],
    hinglish: [
      "Badhai ho {name}! 🏆 Aap {brand} ke {tier} tier mein upgrade ho gaye. Exclusive benefits aur rewards enjoy karein!",
      "Level up! 🎊 {name}, ab aap {tier} member hain. Premium perks unlock: {benefits}. Loyalty ke liye shukriya!",
      "🌟 {name}, aapki loyalty rang layi! {tier} tier mein welcome. Sabhi purchases par {discount}% extra. Shine karte rahein!"
    ]
  },
  cohortSpecific: {
    champions: {
      english: "VIP Alert {name}! 👑 As our Champion customer, enjoy EXCLUSIVE early access to {campaign}. Shop before anyone else!",
      hinglish: "VIP Alert {name}! 👑 Hamare Champion customer ke liye EXCLUSIVE early access to {campaign}. Sabse pehle shopping karein!"
    },
    loyal: {
      english: "Thank you for your loyalty {name}! 💎 Here's a special reward - {points} bonus points. Keep shopping & earning!",
      hinglish: "Aapki loyalty ke liye shukriya {name}! 💎 Special reward - {points} bonus points. Shopping aur earning jaari rakhein!"
    },
    atRisk: {
      english: "We miss you {name}! 💝 Here's 25% OFF + {points} bonus points to welcome you back. Valid 7 days only!",
      hinglish: "Aapki yaad aa rahi hai {name}! 💝 25% OFF + {points} bonus points aapke liye. Sirf 7 din valid!"
    },
    oneTimers: {
      english: "Hey {name}! 🌟 Loved your first visit? Come back for 15% OFF on your 2nd purchase. Let's make it a habit!",
      hinglish: "Hey {name}! 🌟 First visit pasand aayi? 2nd purchase par 15% OFF le jaayein. Shopping habit bana lo!"
    }
  }
};

const categoryIcons = {
  welcome: Gift,
  winback: Heart,
  birthday: Gift,
  pointsExpiry: AlertTriangle,
  campaign: Bell,
  tierUpgrade: Crown,
  cohortSpecific: Target
};

export default function SMSBuilder() {
  const { selectedBrand } = useChat();
  const [category, setCategory] = useState('welcome');
  const [language, setLanguage] = useState('hinglish');
  const [selectedTemplate, setSelectedTemplate] = useState(0);
  const [customMessage, setCustomMessage] = useState('');
  const [cohort, setCohort] = useState('champions');
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState('');

  // Get current templates based on selection
  const getTemplates = () => {
    if (category === 'cohortSpecific') {
      return [smsTemplates.cohortSpecific[cohort]?.[language] || ''];
    }
    return smsTemplates[category]?.[language] || [];
  };

  const templates = getTemplates();

  useEffect(() => {
    if (templates.length > 0) {
      setCustomMessage(templates[0] || '');
      setSelectedTemplate(0);
    }
  }, [category, language, cohort]);

  const handleTemplateSelect = (idx) => {
    setSelectedTemplate(idx);
    setCustomMessage(templates[idx] || '');
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(customMessage);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const generateAISuggestion = async () => {
    setGenerating(true);
    try {
      // Simulate AI generation (in real implementation, call AI API)
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const suggestions = {
        welcome: language === 'hinglish' 
          ? `Welcome to ${selectedBrand?.name || 'Brand'}! 🎉 Aap ab humare family ka hissa hain. Har shopping par points kamayein aur exclusive offers ka maza lein!`
          : `Welcome to ${selectedBrand?.name || 'Brand'}! 🎉 You're now part of our family. Earn points on every purchase and enjoy exclusive member benefits!`,
        winback: language === 'hinglish'
          ? `Kahan ho ${selectedBrand?.name || 'Brand'} ke pyaare customer? Bahut yaad aa rahi hai! Wapas aao aur 30% OFF lo - sirf aapke liye!`
          : `Where have you been? We've missed you at ${selectedBrand?.name || 'Brand'}! Come back for an exclusive 30% OFF - just for you!`,
        cohortSpecific: language === 'hinglish'
          ? `Special ${cohort} member ke liye: ${selectedBrand?.name || 'Brand'} par aaj hi shopping karein aur double rewards paayein!`
          : `Exclusive for ${cohort} members: Shop at ${selectedBrand?.name || 'Brand'} today and earn double rewards!`
      };
      
      setAiSuggestion(suggestions[category] || suggestions.welcome);
    } catch (err) {
      console.error('AI generation error:', err);
    } finally {
      setGenerating(false);
    }
  };

  const useAISuggestion = () => {
    setCustomMessage(aiSuggestion);
    setAiSuggestion('');
  };

  const charCount = customMessage.length;
  const smsCount = Math.ceil(charCount / 160);

  return (
    <div className="space-y-6" data-testid="sms-builder">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">SMS Content Builder</h2>
          <p className="text-muted-foreground">Create engaging SMS campaigns in English & Hinglish</p>
        </div>
        <Button onClick={generateAISuggestion} disabled={generating} className="gap-2">
          {generating ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Sparkles className="w-4 h-4" />
          )}
          AI Suggest
        </Button>
      </div>

      {/* AI Suggestion */}
      {aiSuggestion && (
        <Card className="bg-gradient-to-r from-primary/10 via-secondary/5 to-accent/10 border-primary/20">
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold mb-2">AI Suggestion</h3>
                <p className="text-sm mb-3">{aiSuggestion}</p>
                <Button size="sm" onClick={useAISuggestion}>Use This</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Configuration */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Language */}
              <div>
                <label className="text-sm font-medium mb-2 block">Language</label>
                <Tabs value={language} onValueChange={setLanguage}>
                  <TabsList className="w-full">
                    <TabsTrigger value="english" className="flex-1">English</TabsTrigger>
                    <TabsTrigger value="hinglish" className="flex-1">Hinglish</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>

              {/* Category */}
              <div>
                <label className="text-sm font-medium mb-2 block">Category</label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="welcome">🎉 Welcome</SelectItem>
                    <SelectItem value="winback">💝 Win-back</SelectItem>
                    <SelectItem value="birthday">🎂 Birthday</SelectItem>
                    <SelectItem value="pointsExpiry">⚠️ Points Expiry</SelectItem>
                    <SelectItem value="campaign">🔥 Campaign</SelectItem>
                    <SelectItem value="tierUpgrade">👑 Tier Upgrade</SelectItem>
                    <SelectItem value="cohortSpecific">🎯 Cohort Specific</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Cohort selector for cohort-specific */}
              {category === 'cohortSpecific' && (
                <div>
                  <label className="text-sm font-medium mb-2 block">Target Cohort</label>
                  <Select value={cohort} onValueChange={setCohort}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="champions">🏆 Champions</SelectItem>
                      <SelectItem value="loyal">💎 Loyal</SelectItem>
                      <SelectItem value="atRisk">⚠️ At Risk</SelectItem>
                      <SelectItem value="oneTimers">🌟 One-Timers</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Templates */}
          {category !== 'cohortSpecific' && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Templates</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {templates.map((template, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleTemplateSelect(idx)}
                    className={`w-full p-3 rounded-lg text-left text-sm transition-all ${
                      selectedTemplate === idx
                        ? 'bg-primary/10 border border-primary/30'
                        : 'bg-muted/50 hover:bg-muted'
                    }`}
                  >
                    <p className="line-clamp-2">{template}</p>
                  </button>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Editor */}
        <div className="lg:col-span-2">
          <Card className="h-full">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Message Editor</CardTitle>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span>{charCount} chars</span>
                  <span>{smsCount} SMS</span>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                placeholder="Type your SMS message here..."
                className="min-h-[200px] text-base"
              />

              {/* Variable hints */}
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-xs font-medium mb-2">Available Variables:</p>
                <div className="flex flex-wrap gap-2">
                  {['{name}', '{brand}', '{points}', '{date}', '{code}', '{discount}', '{tier}', '{campaign}'].map(v => (
                    <code 
                      key={v} 
                      className="px-2 py-1 bg-background rounded text-xs cursor-pointer hover:bg-primary/10"
                      onClick={() => setCustomMessage(prev => prev + ' ' + v)}
                    >
                      {v}
                    </code>
                  ))}
                </div>
              </div>

              {/* Preview */}
              <div>
                <p className="text-sm font-medium mb-2">Preview</p>
                <div className="p-4 rounded-lg bg-muted/30 border">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                      <MessageSquare className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium mb-1">{selectedBrand?.name || 'Brand'}</p>
                      <p className="text-sm">
                        {customMessage
                          .replace(/{name}/g, 'Priya')
                          .replace(/{brand}/g, selectedBrand?.name || 'Brand')
                          .replace(/{points}/g, '500')
                          .replace(/{date}/g, '31 Jan 2026')
                          .replace(/{code}/g, 'SAVE20')
                          .replace(/{discount}/g, '20')
                          .replace(/{tier}/g, 'Gold')
                          .replace(/{campaign}/g, 'New Year Sale')
                          .replace(/{id}/g, 'MEM12345')
                          .replace(/{benefits}/g, 'Free shipping, Priority support')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setCustomMessage('')}>
                  Clear
                </Button>
                <Button variant="outline" onClick={handleCopy} className="gap-2">
                  {copied ? (
                    <>
                      <Check className="w-4 h-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy
                    </>
                  )}
                </Button>
                <Button className="gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Save Template
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
